import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { fetchAccountLinksWithCompanyType } from '../api'
import { fetchAllCompaniesConnected } from 'src/Libs/service-dashboard/api/'

import { CompanyConnectedModel, CompanyWithAccountLinkData } from 'src/Libs/service-dashboard/models'
import { CACHE_TIME } from 'src/Libs/installations/constants'
import { useEffect, useState } from 'react'
import { AccountLinkModel } from 'src/Libs/installation-rights-management/models'
import { usePortalQuery } from 'src/Libs/api'

type RefetchFunction = () => void

export interface UseAttachedCompanies {
  isLoading: boolean
  isError: boolean
  data: CompanyWithAccountLinkData[]
  refetch: RefetchFunction
}

/**
 * Hook for getting all attached companies that attached to installation(installers, owners)
 */
export const useAttachedCompanies = (installationId: string | undefined, enabled = true): UseAttachedCompanies => {
  const [attachedCompaniesWithAccessRights, setAttachedCompaniesWithAccessRights] = useState<CompanyWithAccountLinkData[]>([])
  const authFetch = useAuthFetchWithResponse()

  const accountLinkDataQueryFunction = async (): Promise<AccountLinkModel[]> => {
    const { hasErrors, error, value } = await fetchAccountLinksWithCompanyType(authFetch, installationId)

    if (hasErrors) {
      throw error || new Error('Error fetching account links')
    }

    return value ?? []
  }

  const {
    data: accountLinksData,
    isLoading: accountLinksLoading,
    isError: accountLinksError,
    refetch,
  } = usePortalQuery({
    queryKey: ['installations', installationId, 'account-links', 'accountType=company'],
    queryFn: accountLinkDataQueryFunction,
    enabled: !!installationId && enabled,
    staleTime: CACHE_TIME,
  })

  const accountLinkIds = accountLinksData?.map(elem => elem.accountId).join(',')

  const attachedCompaniesQueryFunction = async (): Promise<CompanyConnectedModel[]> => {
    const { error, hasErrors, value } = await fetchAllCompaniesConnected(authFetch, { filterOr: `firma.ID=IN(${accountLinkIds})` })

    if (hasErrors) {
      throw error || new Error('Error fetching attached companies')
    }

    return value ?? []
  }

  const {
    data: attachedCompanies,
    isLoading: attachedCompaniesLoading,
    isError: attachedCompaniesError,
  } = usePortalQuery({
    queryKey: ['users', 'companies', `firma.ID=${accountLinkIds}`],
    queryFn: attachedCompaniesQueryFunction,
    enabled: !!accountLinkIds,
    staleTime: CACHE_TIME,
  })

  useEffect(() => {
    const companiesWithAccessRights = attachedCompanies?.map(company => {
      const accountLink = accountLinksData?.find(link => link.accountId.toString() === company.companyId.toString())

      return {
        ...company,
        hasReadAccess: accountLink?.hasReadAccess ?? false,
        hasWriteAccess: accountLink?.hasWriteAccess ?? false,
        startAt: accountLink?.startAt,
      } as CompanyWithAccountLinkData
    })

    setAttachedCompaniesWithAccessRights(companiesWithAccessRights ?? [])
  }, [attachedCompanies, accountLinksData])

  return {
    data: attachedCompaniesWithAccessRights,
    isLoading: accountLinksLoading || attachedCompaniesLoading,
    isError: accountLinksError || attachedCompaniesError,
    refetch: refetch as RefetchFunction,
  }
}
